export type UpdateType = 'Progress' | 'Issue' | 'Inspection' | 'Complete' | 'Delay';
export type PhotoTag = 'Before' | 'Progress' | 'After' | 'Issue';
export type UpdateRole = 'Management' | 'Subcontractor' | 'Client';

export interface PhotoItem {
  id: string;
  url: string;
  tag: PhotoTag;
  caption: string;
  uploadedBy: UpdateRole;
  uploadedAt: string;
  stage: string;
}

export interface SiteUpdate {
  id: string;
  type: UpdateType;
  title: string;
  body: string;
  role: UpdateRole;
  stage: string;
  timestamp: string;
  projectId: string;
  photos: PhotoItem[];
  visibility: 'All Parties' | 'Management + Subcontractor' | 'Management Only';
}

export const mockPhotos: PhotoItem[] = [
  {
    id: 'ph1',
    url: 'https://readdy.ai/api/search-image?query=kitchen%20renovation%20before%20state%20old%20cabinets%20dated%20New%20Zealand%20home%20interior%20natural%20light%20professional%20photography&width=600&height=450&seq=photo001&orientation=landscape',
    tag: 'Before',
    caption: 'Kitchen — before demolition',
    uploadedBy: 'Management',
    uploadedAt: '8 Jan 2024',
    stage: 'Demolition',
  },
  {
    id: 'ph2',
    url: 'https://readdy.ai/api/search-image?query=kitchen%20demolition%20stripped%20walls%20exposed%20framing%20renovation%20in%20progress%20New%20Zealand%20residential%20professional%20photography&width=600&height=450&seq=photo002&orientation=landscape',
    tag: 'Progress',
    caption: 'Demolition complete — framing exposed',
    uploadedBy: 'Subcontractor',
    uploadedAt: '19 Jan 2024',
    stage: 'Demolition',
  },
  {
    id: 'ph3',
    url: 'https://readdy.ai/api/search-image?query=new%20timber%20framing%20structure%20residential%20renovation%20New%20Zealand%20interior%20walls%20construction%20professional%20photography%20bright&width=600&height=450&seq=photo003&orientation=landscape',
    tag: 'Progress',
    caption: 'New framing and LVL beam installed',
    uploadedBy: 'Subcontractor',
    uploadedAt: '2 Feb 2024',
    stage: 'Structure',
  },
  {
    id: 'ph4',
    url: 'https://readdy.ai/api/search-image?query=plumbing%20rough%20in%20pipes%20residential%20bathroom%20renovation%20New%20Zealand%20construction%20professional%20photography%20clean%20background&width=600&height=450&seq=photo004&orientation=landscape',
    tag: 'Progress',
    caption: 'Plumbing rough-in — wet areas',
    uploadedBy: 'Subcontractor',
    uploadedAt: '20 Feb 2024',
    stage: 'Rough-in',
  },
  {
    id: 'ph5',
    url: 'https://readdy.ai/api/search-image?query=electrical%20wiring%20rough%20in%20residential%20renovation%20New%20Zealand%20interior%20walls%20professional%20photography%20construction%20site&width=600&height=450&seq=photo005&orientation=landscape',
    tag: 'Progress',
    caption: 'Electrical rough-in complete',
    uploadedBy: 'Subcontractor',
    uploadedAt: '28 Feb 2024',
    stage: 'Rough-in',
  },
  {
    id: 'ph6',
    url: 'https://readdy.ai/api/search-image?query=kitchen%20cabinetry%20installation%20modern%20white%20joinery%20New%20Zealand%20home%20renovation%20professional%20photography%20clean%20bright%20interior&width=600&height=450&seq=photo006&orientation=landscape',
    tag: 'Progress',
    caption: 'Kitchen joinery installation underway',
    uploadedBy: 'Subcontractor',
    uploadedAt: '15 Mar 2024',
    stage: 'Fit-out',
  },
  {
    id: 'ph7',
    url: 'https://readdy.ai/api/search-image?query=bathroom%20tile%20installation%20progress%20modern%20white%20tiles%20New%20Zealand%20renovation%20professional%20photography%20clean%20interior&width=600&height=450&seq=photo007&orientation=landscape',
    tag: 'Progress',
    caption: 'Bathroom tiling in progress',
    uploadedBy: 'Subcontractor',
    uploadedAt: '22 Mar 2024',
    stage: 'Fit-out',
  },
  {
    id: 'ph8',
    url: 'https://readdy.ai/api/search-image?query=weatherboard%20damage%20issue%20exterior%20cladding%20New%20Zealand%20home%20renovation%20problem%20professional%20photography%20close%20up&width=600&height=450&seq=photo008&orientation=landscape',
    tag: 'Issue',
    caption: 'Weatherboard damage — north elevation',
    uploadedBy: 'Subcontractor',
    uploadedAt: '5 Mar 2024',
    stage: 'Fit-out',
  },
  {
    id: 'ph9',
    url: 'https://readdy.ai/api/search-image?query=completed%20modern%20kitchen%20renovation%20New%20Zealand%20home%20white%20cabinetry%20stone%20benchtop%20professional%20photography%20bright%20clean%20interior&width=600&height=450&seq=photo009&orientation=landscape',
    tag: 'After',
    caption: 'Kitchen — completed fit-out',
    uploadedBy: 'Management',
    uploadedAt: '10 Apr 2024',
    stage: 'Fit-out',
  },
];

export const mockSiteUpdates: SiteUpdate[] = [
  {
    id: 'upd1',
    type: 'Progress',
    title: 'Kitchen joinery installed — benchtop templating complete',
    body: 'Kitchen joinery is fully installed and looking excellent. Benchtop templater was on site this morning — stone benchtop delivery expected in 8 days. Tiler starting on bathroom floor tiles Wednesday. Weatherboard on north elevation is 80% complete.',
    role: 'Subcontractor',
    stage: 'Fit-out',
    timestamp: '18 min ago',
    projectId: 'JH-2024-001',
    photos: [mockPhotos[5], mockPhotos[6]],
    visibility: 'All Parties',
  },
  {
    id: 'upd2',
    type: 'Inspection',
    title: 'Plumbing rough-in inspection passed — no remedial work required',
    body: 'Inspector B. Thompson from Auckland Council attended site at 9am. All wet areas signed off with no remedial work required. CCC pathway remains on track. Next inspection is framing sign-off scheduled for 2 May.',
    role: 'Management',
    stage: 'Rough-in',
    timestamp: '1 hr ago',
    projectId: 'JH-2024-002',
    photos: [mockPhotos[3]],
    visibility: 'Management + Subcontractor',
  },
  {
    id: 'upd3',
    type: 'Issue',
    title: 'Tile delivery delayed 3 days — Calacatta floor tiles on backorder',
    body: 'Supplier has confirmed Calacatta floor tiles are on backorder — delivery pushed from Monday to Thursday. Tiler has been rescheduled to start on bathroom splashback tiles first. No impact to practical completion date at this stage.',
    role: 'Subcontractor',
    stage: 'Fit-out',
    timestamp: '3 hrs ago',
    projectId: 'JH-2024-001',
    photos: [],
    visibility: 'Management + Subcontractor',
  },
  {
    id: 'upd4',
    type: 'Delay',
    title: 'Weatherboard damage found on north elevation — remedial work required',
    body: 'During cladding installation, significant rot damage was found behind the existing weatherboard on the north elevation. Approximately 4.2m² of substrate requires replacement. Remedial work will add 2 days to the cladding schedule. Photos attached.',
    role: 'Subcontractor',
    stage: 'Fit-out',
    timestamp: '1 day ago',
    projectId: 'JH-2024-001',
    photos: [mockPhotos[7]],
    visibility: 'Management + Subcontractor',
  },
];

export const mockInstructions = [
  {
    id: 'ins1',
    title: 'Install Calacatta floor tiles to bathroom — bond pattern',
    stage: 'Fit-out',
    body: 'Install Calacatta marble-look floor tiles to main bathroom in a 45° bond pattern. Grout colour: Mapei Ultracolor Plus FA — Warm Grey. Ensure waterproofing membrane is fully cured before tiling commences. Refer to specification sheet attached.',
    dueDate: '26 Apr 2024',
    status: 'Open' as const,
    urgent: true,
    requiresResponse: true,
    projectId: 'JH-2024-001',
  },
  {
    id: 'ins2',
    title: 'Complete weatherboard replacement — north elevation',
    stage: 'Fit-out',
    body: 'Replace all damaged weatherboard on north elevation. Use Linea 180mm weatherboard — Resene Alabaster finish. Ensure all substrate rot is fully removed and treated before new boards are fixed. Refer to council consent drawings for fixing pattern.',
    dueDate: '19 Apr 2024',
    status: 'In Review' as const,
    urgent: false,
    requiresResponse: false,
    projectId: 'JH-2024-001',
  },
  {
    id: 'ins3',
    title: 'Electrical rough-in — second fix wiring to kitchen',
    stage: 'Rough-in',
    body: 'Complete second fix electrical wiring to kitchen area. Install 4x double GPOs above benchtop, 2x dedicated circuits for oven and dishwasher, and under-cabinet LED strip lighting circuit. Refer to electrical plan Rev C.',
    dueDate: '22 Apr 2024',
    status: 'Open' as const,
    urgent: false,
    requiresResponse: true,
    projectId: 'JH-2024-001',
  },
];
